import type { en } from './en';

/**
 * Turkish message catalog.
 *
 * Typed as the exact key set of `en`, so adding an English key without a
 * Turkish one is a compile error rather than a silent English string in a
 * Turkish panel.
 *
 * Product terms that the team says in English stay in English on purpose —
 * "prompt", "rubric", "blueprint", "worker", "fallback". Translating a term
 * the operator only ever reads in the code makes the panel harder to use, not
 * easier.
 */
export const tr: Record<keyof typeof en, string> = {
  // ─── App shell ───────────────────────────────────────────────────────────
  'app.title': 'Prepzio Yönetim',
  'app.nav.toggle': 'Menüyü aç/kapat',
  'app.nav.authoring': 'İçerik üretimi',
  'app.nav.learners': 'Öğrenenler',
  'app.nav.productDocument': 'Ürün dokümanı',
  'app.nav.platform': 'Platform',
  'app.signOut': 'Çıkış yap',
  'app.colorScheme.toggle': 'Renk temasını değiştir',
  'app.colorScheme.switchTo': '{mode} temaya geç',
  'app.colorScheme.light': 'açık',
  'app.colorScheme.dark': 'koyu',
  'app.language': 'Dil',
  'app.language.change': 'Dili değiştir',

  // ─── Sign-in ─────────────────────────────────────────────────────────────
  'signIn.subtitle': 'Dahili soru üretim aracı. Yalnızca yöneticiler erişebilir.',
  'signIn.google': 'Google ile giriş yap',
  'signIn.or': 'veya',
  'signIn.email': 'E-posta',
  'signIn.password': 'Parola',
  'signIn.submit': 'E-posta ile giriş yap',
  'signIn.failed': 'Giriş başarısız',

  // ─── Model badge ─────────────────────────────────────────────────────────
  'model.withFallback': 'Üretim modeli: {primary} (yedek: {fallback})',
  'model.noFallback':
    'Üretim modeli: {primary}. Yedek sağlayıcı tanımlı değil; bu yüzden tüm katmanlar tek sağlayıcı üzerinde ikinci bir şans olmadan çalışıyor ve iş ilanı ekran görüntüsü okuma (vision) kullanılamıyor. İkisini de açmak için GEMINI_API_KEY worker secret değerini tanımlayın.',
  'model.noFallbackShort': 'yedek yok',

  // ─── Screen titles (also the nav labels) ─────────────────────────────────
  'screen.generate': 'Üretim',
  'screen.questions': 'Soru bankası',
  'screen.itemModels': 'Soru şablonları',
  'screen.skills': 'Yetkinlikler',
  'screen.skillQueue': 'Yetkinlik kuyruğu',
  'screen.reviewQueue': 'İnceleme kuyruğu',
  'screen.bankHealth': 'Banka sağlığı',
  'screen.questionFlow': 'Soru akışı',
  'screen.launchReadiness': 'Yayın hazırlığı',
  'screen.users': 'Kullanıcılar',
  'screen.costs': 'Maliyetler',
  'screen.businessRules': 'İş kuralları',
  'screen.userFlow': 'Kullanıcı akışı',
  'screen.traceability': 'İzlenebilirlik',
  'screen.health': 'Sistem sağlığı',
  'screen.config': 'Ayarlar',
  'screen.simulate': 'Simülasyon',
  'screen.database': 'Veritabanı',
  'screen.emails': 'E-postalar',
  'screen.guide': 'Kılavuz',
  'screen.review': 'İnceleme',
  'screen.content': 'İçerik',
  'screen.roadmaps': 'Yol haritaları',

  // ─── Common vocabulary ───────────────────────────────────────────────────
  'common.loading': 'Yükleniyor…',
  'common.save': 'Kaydet',
  'common.saving': 'Kaydediliyor…',
  'common.saved': 'Kaydedildi',
  'common.cancel': 'Vazgeç',
  'common.close': 'Kapat',
  'common.delete': 'Sil',
  'common.edit': 'Düzenle',
  'common.refresh': 'Yenile',
  'common.retry': 'Yeniden dene',
  'common.search': 'Ara',
  'common.filter': 'Filtrele',
  'common.all': 'Tümü',
  'common.none': 'Yok',
  'common.yes': 'Evet',
  'common.no': 'Hayır',
  'common.error': 'Hata',
  'common.empty': 'Burada henüz bir şey yok.',
  'common.copy': 'Kopyala',
  'common.copied': 'Kopyalandı',
  'common.open': 'Aç',
  'common.back': 'Geri',
  'common.next': 'İleri',
  'common.previous': 'Önceki',
  'common.apply': 'Uygula',
  'common.reset': 'Sıfırla',
  'common.skill': 'Yetkinlik',
  'common.level': 'Seviye',
  'common.status': 'Durum',
  'common.count': 'Adet',
  'common.total': 'Toplam',
  'common.actions': 'İşlemler',
  'common.details': 'Ayrıntılar',
  'common.unknown': 'Bilinmiyor',
  'common.never': 'Hiç',

  // ─── Error boundary ──────────────────────────────────────────────────────
  'errorBoundary.title': 'Bu ekran çöktü',
  'errorBoundary.retry': 'Tekrar dene',
  'errorBoundary.home': 'Ana ekrana dön',

  // ─── Seeded-content lock ─────────────────────────────────────────────────
  'seededLock.tooltip':
    '{what} depoda sürümleniyor — {file} dosyasını düzenleyip bir pull request açın. Kararlar (onaylar, kural tipi, tazelik damgası) yine burada veriliyor.',
  'seededLock.aria': '{what} {file} içinde düzenleniyor',

  // ─── Database ────────────────────────────────────────────────────────────
  'database.intro':
    'Canlı D1 üzerinde ham SQL konsolu (açık kaynak LibSQL/Outerbase Studio). UPDATE/DELETE/DDL dahil tam okuma/yazma — geri alma yok, yıkıcı sorguları iki kez kontrol edin.',
  'database.frameTitle': 'Veritabanı konsolu',
  'database.queryFailed': 'Sorgu başarısız',

  // ─── Roadmaps ────────────────────────────────────────────────────────────
  'roadmaps.intro':
    'Bir yol haritasının yapısını zaman çizelgesi olarak inceleyin: 7 günlük planın durakları (ana hedef · pratik · değerlendirme), bunların doğurduğu pratik oturumları ve her oturumun sıralı soruları.',
  'roadmaps.select': 'Yol haritası',
  'roadmaps.none': 'Yol haritası yok',
  'roadmaps.hideDiagram': 'Diyagramı gizle',
  'roadmaps.showDiagram': 'Diyagram olarak gör',
  'roadmaps.createdAt': '{date} tarihinde oluşturuldu',
  'roadmaps.deleteSim': 'Simülasyonu sil',
  'roadmaps.deleteSimConfirm':
    'Bu simülasyon yol haritası ve ona ait sentetik kullanıcı silinsin mi?',
  'roadmaps.stations': '{count} durak',
  'roadmaps.sessions': '{count} oturum',
  'roadmaps.questionUses': '{count} soru kullanımı',

  // ─── Roadmap view ────────────────────────────────────────────────────────
  'roadmapView.empty': 'Bu yol haritasında henüz durak veya oturum yok.',
  'roadmapView.assessment': 'Değerlendirme',
  'roadmapView.questionsInRoadmap': 'Bu yol haritasındaki sorular',
  'roadmapView.assessmentHint':
    'Aşağıdaki gün durakları planın taslağıdır (öğrenen pratik yapana kadar sabit soru yoktur). Bunlar, bu yol haritasının üretildiği başlangıç değerlendirmesinin sorularıdır — tüm ayrıntısını (şıklar, ipuçları, rubrik) görmek için bir soruya tıklayın.',
  'roadmapView.day': '{day}. gün',
  'roadmapView.minutes': '~{minutes} dk',
  'roadmapView.questionCount': '{count} soru',
  'roadmapView.questionCount_one': '1 soru',

  // ─── Skill picker ────────────────────────────────────────────────────────
  'skillPicker.placeholder': 'Yetkinlik ara…',
  'skillPicker.searching': 'Aranıyor…',
  'skillPicker.noMatch': 'Eşleşen yetkinlik yok',
  'skillPicker.hintFiltered': '{total} eşleşmeden {shown} tanesi — daraltmak için yazmaya devam',
  'skillPicker.hintAll': '{total} yetkinlikten {shown} tanesi — aramak için yazın',

  // ─── Question inspector ──────────────────────────────────────────────────
  'question.detail': 'Soru ayrıntısı',
  'question.judge': 'hakem {score}',
  'question.prompt': 'Soru metni',
  'question.options': 'Şıklar',
  'question.expectedConcepts': 'Beklenen kavramlar',
  'question.hintLadder': 'İpucu merdiveni',
  'question.exampleStrongAnswer': 'Örnek güçlü cevap',
  'question.commonMistakes': 'Sık yapılan hatalar',
  'question.rubric': 'Rubrik — {title}',
  'question.criterion': 'Ölçüt',
  'question.description': 'Açıklama',
  'question.max': 'En yüksek',
  'question.judgeRationale': 'Hakem: {rationale}',
  'question.noQuestions': 'Soru yok.',
  'question.viewDetail': 'Sorunun tüm ayrıntısını gör',
  'question.confidence': 'güven {value}/5',
  'question.answer': 'Cevap',

  // ─── Authoring instruction ───────────────────────────────────────────────
  'authoring.title': 'Yazım talimatı',
  'authoring.subtitle':
    'Modele iyi bir sorunun ne olduğu hakkında söylenenler. Çıktının biçimi — ipucu merdiveni, rubrik, JSON — kodda sabittir ve buradan düzenlenemez.',
  'authoring.hide': 'Gizle',
  'authoring.editVersion': 'Düzenle (v{version})',
  'authoring.noneYet':
    'Henüz bir talimat yazılmadı. Yerleşik varsayılan, ilk üretim çalıştığında oluşturulur — bir kez üretin, sonra düzenlemek için buraya dönün.',
  'authoring.saveNote':
    'Kaydetmek v{version} sürümünü oluşturur ve etkinleştirir. Önceki sürümler saklanır; böylece halihazırda üretilmiş taslaklar hâlâ yazıldıkları metne işaret eder.',
  'authoring.revert': 'Geri al',
  'authoring.saveAs': 'v{version} olarak kaydet',

  // ─── Sparkline ───────────────────────────────────────────────────────────
  'sparkline.aria': '{label}: son {last}, zirve {peak}',
  'sparkline.title': '{label} — son {last}, zirve {peak}, {count} saatlik nokta',
  'sparkline.noData': 'bu aralıkta veri yok',

  // ─── Domain enums ────────────────────────────────────────────────────────
  'enum.status.published': 'yayında',
  'enum.status.draft': 'taslak',
  'enum.status.rejected': 'reddedildi',
  'enum.type.free_text': 'açık uçlu',
  'enum.type.mcq': 'çoktan seçmeli',
  'enum.type.coding': 'kodlama',
  'enum.difficulty.easy': 'kolay',
  'enum.difficulty.medium': 'orta',
  'enum.difficulty.hard': 'zor',

  // ─── Content ─────────────────────────────────────────────────────────────
  'content.intro':
    'Soru bankasındaki her şey ve durumu. Yayında olan öğrenenlere açıktır; taslak inceleme bekler; reddedilen elenmiştir.',
  'content.bySkill': 'Yetkinliğe göre',
  'content.totals': 'Toplamlar:',
  'content.publishedCount': '{count} yayında',
  'content.draftCount': '{count} taslak',
  'content.rejectedCount': '{count} reddedildi',
  'content.colSkill': 'Yetkinlik',
  'content.colPublished': 'Yayında',
  'content.colDraft': 'Taslak',
  'content.colRejected': 'Reddedildi',
  'content.noSkills': 'Yetkinlik yok.',
  'content.browse': 'Gözat',
  'content.allSkills': 'Tüm yetkinlikler',
  'content.type': 'Tür',
  'content.difficulty': 'Zorluk',
  'content.nothingHere': 'Burada bir şey yok.',
  'content.nearDup': 'yakın kopya',
  'content.expected': 'Beklenen: {concepts}',
  'content.first50': 'İlk 50 gösteriliyor — daha fazlası için filtreleri daraltın.',

  // ─── Config ──────────────────────────────────────────────────────────────
  'config.intro':
    'Ayarlanabilir alan kuralları ve özellik bayrakları — burada düzenlenir, API tarafından canlı okunur (deploy gerekmez). Temizlenirse her değer koda gömülü varsayılanına döner.',
  'config.infoTip':
    'Bunlar değişen iş kurallarıdır. Worker, çalışma anında KV üzerinden geçersiz kılma ?? varsayılan okur — eksik veya hatalı bir geçersiz kılma her zaman kod varsayılanına döner, dolayısıyla ayarlar doğruluğu bozamaz. Değişmezler (kimlik doğrulama, teklik, iş akışı) burada değil, kodda uygulanır.',
  'config.overridden': 'geçersiz kılındı',
  'config.default': 'varsayılan',
  'config.defaultValue': 'varsayılan: {value}',
  'config.on': 'açık',
  'config.off': 'kapalı',
  'config.unbounded': 'sınırsız',
  'config.integer': 'tam sayı',
  'config.invalidJson': 'Geçersiz JSON',

  // ─── Guide ───────────────────────────────────────────────────────────────
  'guide.intro':
    'Bu araç, soru bankasını elle yazılmış tohum içerikten incelenmiş, LLM ile üretilmiş içeriğe dönüştürür. Akış her zaman şudur: üret → incele → onayla. Ürettiğiniz hiçbir şey siz onaylamadan öğrenenlere gösterilmez.',

  'guide.access.title': '0 · Giriş ve erişim',
  'guide.access.1': 'Yönetici izin listesindeki Google hesabıyla giriş yapın.',
  'guide.access.2':
    "Giriş yapabiliyor ama her işlemde “Admin access required” yanıtı alıyorsanız, e-postanız API'nin ADMIN_EMAILS listesinde değildir — eklenmesini isteyin.",

  'guide.templates.title': '1 · Prompt şablonları (önce bunu yapın)',
  'guide.templates.intro':
    'Üretim için etkin bir question_generation şablonu gerekir. Prompt şablonları sekmesini açın:',
  'guide.templates.1':
    "Yapay zekâ ile taslak: tek satırlık bir özet yazın (örn. “kıdemli backend, katı JSON, zor sorular”) ve Yapay zekâ ile taslak'a basın — model bunu editörde tam bir şablon gövdesine genişletir, siz de düzenleyip kaydedersiniz.",
  'guide.templates.2':
    'Yazım talimatlarını gövdeye yazın ve Yeni sürüm olarak kaydet deyin. İlk sürüm otomatik olarak etkindir; sonraki sürümler pasif başlar.',
  'guide.templates.3':
    'Üretimin kullanmasını istediğiniz sürümü Etkinleştirin. Şablonlar değiştirilemez — bir sürümü asla düzenlemezsiniz, yenisini kaydedip etkinleştirirsiniz.',
  'guide.templates.4':
    'Editörü en son gövdeyle doldurup üzerinde çalışmak için “ad” Düzenle seçeneğini kullanın.',

  'guide.generate.title': '2 · Taslak üretimi',
  'guide.generate.intro': 'Üretim sekmesinde — asıl iş budur:',
  'guide.generate.1':
    "Yetkinlik · tür · zorluk · adet seçin (ya da şablonu “Etkin varsayılan” bırakın). Üret'e basın.",
  'guide.generate.2':
    "Öneriler — kapsam boşlukları: liste, hangi (yetkinlik × zorluk) hücrelerinin her biri için 5 yayında soru hedefinin altında olduğunu en acilden başlayarak gösterir. O hücre için formu doldurmak üzere Kullan'a tıklayın.",
  'guide.generate.3':
    'Sonuç paneli token/maliyeti ve iki kapı sonucunu bildirir: biçimsiz-reddedildi (hatalı biçim, otomatik elendi) ve yakın kopya işaretlendi (mevcut bir soruya benziyor). Geri kalan her şey taslak olarak İncelemeye düşer.',
  'guide.generate.note':
    'Üretim para harcar ve hız/bütçe sınırlıdır. “Daily LLM budget reached” uyarısını alırsanız daha sonra tekrar deneyin.',

  'guide.review.title': '3 · İnceleme, düzenleme ve onay',
  'guide.review.intro':
    'İnceleme sekmesi kuyruktur. Önce ilgi gerektirenler sıralanır — yakın kopyalar, sonra hakem görmemişler, sonra en düşük kalite puanı. Her kart tavsiye niteliğinde rozetler gösterir:',
  'guide.review.1':
    'hakem 0,xx — otomatik bir kalite puanı (0–1). ≥ 0,60 “muhtemelen iyi” demektir (yeşil); altı yakından bakmayı hak eder (amber). Hakemin gerekçesi rozetlerin altında gösterilir.',
  'guide.review.2':
    'yakın kopya — taslak mevcut bir soruya benziyor; eşleşen sorunun kimliği gösterilir. Gerçekten farklı değilse reddedin.',
  'guide.review.3':
    'biçimsiz — hatalı biçim nedeniyle otomatik reddedildi (reddedildi durum filtresi altında görünür).',
  'guide.review.advisory':
    'Bu sinyaller tavsiye niteliğindedir — sizin yerinize asla onaylamaz ya da reddetmez. Kararı siz verirsiniz. Her taslak için şunları yapabilirsiniz:',
  'guide.review.4':
    'Soru metnini, kavramları, 4 seviyeli ipucu merdivenini, örnek cevabı, hataları ve rubriği yerinde Düzenleyin, sonra Değişiklikleri kaydedin.',
  'guide.review.5':
    'Onayla → soruyu canlı bankaya yayımlar (artık öğrenenlere gelebilir). İçeriği canlıya alan tek adım budur.',
  'guide.review.6': 'Reddet → taslağı eler.',
  'guide.review.note':
    'Taslak, yayında veya reddedilmiş kayıtları incelemek için durum filtresini kullanın.',

  'guide.reset.title': '3b · İçeriği sıfırlama (tehlikeli bölge)',
  'guide.reset.intro':
    'Sıfırla sekmesi, bir yetkinliğe (ya da tüm taslak deposuna) baştan başlamak istediğinizde üretilmiş soruları toplu siler:',
  'guide.reset.1':
    'Yalnızca taslak ve reddedilmiş kayıtları kaldırır. Yayındaki sorular ve bir öğrenenin gördüğü hiçbir şey buradan silinemez — bunu sadece arayüz değil, API uygular.',
  'guide.reset.2':
    'Kapsamı durum · yetkinlik · zorluk · tür ile daraltın (hepsi isteğe bağlı ve birlikte çalışır) ya da tüm taslak + reddedilmiş içeriği temizlemek için her şeyi varsayılanda bırakın.',
  'guide.reset.3':
    'Her zaman önce Önizleyin — silinecek tam sayıyı gösterir. Sil düğmesi siz onay kutusunu işaretleyene kadar pasif kalır ve kaldırmadan önce bir kez daha sorar. Silme kalıcıdır.',

  'guide.content.title': '3c · İçerik (bankada ne var)',
  'guide.content.body':
    'İçerik sekmesi tüm soru bankasının salt okunur bir özetidir: yetkinlik başına sayım matrisi (yayında · taslak · reddedildi) ve durum · yetkinlik · tür · zorluk ile filtrelenebilir bir liste. Neyin hangi durumda olduğunu görmek için kullanın; düzenleme/onay İncelemede, silme Sıfırlada yapılır.',
  'guide.content.note':
    'Etkin üretim modeli her zaman sağ üst başlıkta bir rozet olarak gösterilir.',

  'guide.roadmaps.title': '3d · Yol haritaları (ilişki diyagramı)',
  'guide.roadmaps.body':
    'Yol haritaları sekmesi bir öğrenenin 7 günlük planını diyagram olarak görselleştirir: bir yol haritası seçin ve duraklarını (her günün ana hedefi · pratiği · değerlendirmesi), bu durakların doğurduğu pratik oturumlarını ve her oturumun sıralı sorularını görün — böylece hangi sorunun nerede ve hangi sırayla kullanıldığını tam olarak görebilirsiniz. “Mermaid kaynağını gör” ham diyagram tanımını gösterir.',

  'guide.goal.title': '4 · Hedef (kapsam matrisi)',
  'guide.goal.body':
    'Hedef, her (yetkinlik × zorluk) hücresinde ≥ 5 yayında soru ve hücre başına en az bir çoktan seçmeli sorudur. Öneriler — kapsam boşlukları listesini yukarıdan aşağı çalışın; liste boşaldığında v0 matrisi dolmuş demektir.',

  'guide.emails.title': '5 · E-postalar (işlemsel)',
  'guide.emails.1':
    'Düz mod: konu + mesaj (isteğe bağlı HTML). Şablon modu: yayımlanmış bir Resend şablonu seçin ve değişkenlerini doldurun (zorunlu olanlar * ile işaretlidir).',
  'guide.emails.2':
    "Her gönderim bir idempotency anahtarı kullanır, böylece çift tıklama iki kez göndermez. Yeni bir gönderime başlamak için Yeni anahtar'a basın.",
  'guide.emails.3':
    'Gönderim geçmişi durumu gösterir (kuyrukta → iletildi / geri döndü / …); duruma veya alıcıya göre filtreleyin ve sayfalamak için “Daha fazla yükle” deyin.',

  'guide.trouble.title': 'Sorun giderme',
  'guide.trouble.1':
    '“No active question_generation template” → Prompt şablonlarında bir tane oluşturup etkinleştirin (adım 1).',
  'guide.trouble.2':
    '“Daily LLM budget reached” → günlük üretim bütçesi tükendi; daha sonra tekrar deneyin.',
  'guide.trouble.3': '“Admin access required” → e-postanız izin listesinde değil.',
  'guide.trouble.4':
    'Model geçerli bir şey döndürmedi → tekrar deneyin veya etkin prompt şablonunu iyileştirin.',

  // ─── Bank health ─────────────────────────────────────────────────────────
  'bankHealth.liveItems': 'Canlı soru',
  'bankHealth.answersCollected': 'Toplanan cevap',
  'bankHealth.medianAnswerTime': 'Ortanca cevap süresi',
  'bankHealth.needsLook': 'Bakılması gerekenler',
  'bankHealth.needsLookHint':
    'Gerçek cevaplardan çıkan bulgular. Burada hiçbir şey değiştirilmedi — karar sizin.',
  'bankHealth.allHealthy':
    'Hiçbir soru hakkında bulgu yok. Bu ya sağlıklı ya da genç bir banka demektir: bir soru hakkında bir şey söylenebilmesi için önce yeterince cevaplanması gerekir.',
  'bankHealth.colQuestion': 'Soru',
  'bankHealth.colFindings': 'Bulgular',
  'bankHealth.colAnswers': 'Cevap',
  'bankHealth.colExposure': 'Maruziyet',
  'bankHealth.mostAsked': 'En çok sorulanlar',
  'bankHealth.mostAskedHint':
    'Her sorunun aldığı cevapların tüm cevaplara oranı. Bu bir hata listesi değildir — aynı soruların her öğrenene ulaştığı yer burasıdır; çözümü kapsam raporudur.',
  'bankHealth.review': 'İncele',
  'bankHealth.retire': 'Emekliye ayır',
  'bankHealth.retireTitle': 'Bu soruyu emekliye ayır',
  'bankHealth.sendForReview': 'İncelemeye gönder',
  'bankHealth.measurementSays': 'Ölçüm ne diyor',
  'bankHealth.retireNote':
    'Hiçbir şey silinmez. Bu soru gerçek öğrenenler tarafından cevaplandı ve onların nasıl ölçüldüğünün bir parçası olarak kalır — emekliye ayırmak yalnızca yeniden sorulmasını durdurur ve inceleme yoluyla geri getirilebilir.',
  'bankHealth.why': 'Gerekçe',
  'bankHealth.whyHint':
    'Bu sorudaki otomatik notun yerini alır; böylece sonraki okuyucu bir insanın baktığını bilir.',
  'bankHealth.severity.critical': 'kritik',
  'bankHealth.severity.warning': 'uyarı',
  'bankHealth.lifecycle.published': 'yayında',
  'bankHealth.lifecycle.pretest': 'ön test',
  'bankHealth.lifecycle.under_review': 'incelemede',
  'bankHealth.lifecycle.retired': 'emekli',

  // ─── Rejection reasons (shared: question flow, review queue) ─────────────
  'reason.factually_wrong': 'Olgusal hata',
  'reason.ambiguous': 'Belirsiz',
  'reason.too_easy': 'Fazla kolay',
  'reason.too_hard': 'Fazla zor',
  'reason.off_skill': 'Bu yetkinlikle ilgili değil',
  'reason.duplicate': 'Tekrar',
  'reason.badly_worded': 'Kötü ifade edilmiş',
  'reason.biased': 'Yanlı veya dışlayıcı',
  'reason.judge_blocked': 'Hakem durdurdu',
  'reason.other': 'Diğer',

  // ─── Question flow ───────────────────────────────────────────────────────
  'flow.intro':
    'Bir sorunun doğduğu andan emekli edildiği ana kadar geçtiği hat: hangi hücrenin doldurulacağı nasıl seçilir, üretim neye dayanır, hangi kapılar eler, insan nerede karar verir, zorluk ne zaman ölçülür. Buradaki her sayı bankanın bugünkü hâlinden okunur — hiçbiri elle yazılmaz.',
  'flow.loadFailed': 'Soru hattı okunamadı',
  'flow.published': 'Yayında:',
  'flow.generated': 'Üretilmiş:',
  'flow.rejected': 'Elenmiş:',
  'flow.waiting': '{count} bekliyor',
  'flow.discardedCount': '{count} elendi',
  'flow.discardedNode': 'Elenen',
  'flow.bottleneck': 'Darboğaz',
  'flow.bottleneckBody': 'En çok madde {step} adımında birikmiş: {count} madde bekliyor. {where}',
  'flow.bottleneckWhere': 'Karar o ekranda veriliyor.',
  'flow.noBottleneck': 'Hiçbir adımda bekleyen madde yok.',
  'flow.section1': '01 · Hat',
  'flow.section1Hint':
    'Kesikli oklar hattı terk eden maddeleri gösterir: üç adım bir maddeyi eleyebilir, geri kalanı yalnızca ilerletir.',
  'flow.section2': '02 · Adımlar',
  'flow.section2Hint':
    'Her adımın kendi sayıları ve kararın verildiği ekran. Sıralama adımının bekleyeni yoktur — kuyruğu, kataloğun henüz doldurulmamış her hücresidir.',
  'flow.section3': '03 · Ne, nerede elendi',
  'flow.section3Hint':
    'Tek bir reddin değeri yok; tekrar eden bir sebep prompt şablonunu değiştirme talimatıdır.',
  'flow.noRejections': 'Henüz elenen madde yok.',
  'flow.colReason': 'Sebep',
  'flow.colStep': 'Eleyen adım',
  'flow.colCount': 'Adet',
  'flow.badlyWordedNote':
    '“Kötü ifade edilmiş” iki kaynaktan gelebilir ve ikisi ayrılamıyor: biçim kapısı bozuk bir üretimi bu sebeple reddeder, aynı sebep incelemecinin kapalı listesinde de vardır. Burada biçim kapısına yazılıyor; /review-stats ise onu insan reddi tarafında sayıyor. İki ekranın aynı bankada farklı konuşmaması için sebep değil, tanım paylaşılıyor.',
  'flow.decidesNothing': 'Bu ekran karar vermez',
  'flow.decidesNothingBody':
    'Onay, red ve emeklilik kendi ekranlarında kalır — burada bir maddenin durumu değiştirilemez. Ayrıca bu, User flow’un ürün iddiası değildir: orada ürün sahibinin “çalışıyor / yapılacak / bozuk” beyanı vardır, burada bankanın ölçülmüş hâli.',
  'flow.goToScreen': 'Ekrana git',
  'flow.noScreen': 'Kendi ekranı yok — bu adım repodan yönetiliyor.',

  // ─── Review ──────────────────────────────────────────────────────────────
  'review.judgeTitle': 'Tavsiye niteliğinde, üçün en iyisi yazım hakem puanı (PRE-171)',
  'review.nearDupTitle': 'Yakın kopya olarak işaretlendi (PRE-169)',
  'review.malformed': 'biçimsiz',
  'review.malformedTitle': 'Altın biçim kapısı tarafından otomatik reddedildi (PRE-170)',
  'review.nearDupNote': '⚠ Mevcut bir sorunun yakın kopyası ({id}).',
  'review.conceptsLabel': 'Beklenen kavramlar (virgülle ayrılmış)',
  'review.mistakesLabel': 'Sık yapılan hatalar (virgülle ayrılmış)',
  'review.hintLadderLabel': 'İpucu merdiveni (1 → 4)',
  'review.hintPlaceholder': '{n}. ipucu',
  'review.rubricHeading': 'Rubrik',
  'review.rubricTitlePlaceholder': 'Rubrik başlığı',
  'review.criterionLabel': 'Etiket',
  'review.saveChanges': 'Değişiklikleri kaydet',
  'review.approve': 'Onayla',
  'review.reject': 'Reddet',
  'review.savedNotice': 'Kaydedildi.',

  // ─── Question bank ───────────────────────────────────────────────────────
  'bank.infoTip':
    'yayında, öğrenenlerin çektiği canlı bankadır; taslak inceleme bekler; reddedildi geri çevrilmiş ama saklanmıştır. Tüm ayrıntısını — şıklar, ipuçları, rubrik — görmek için bir soru metnine tıklayın.',
  'bank.range': '{total} kayıttan {from}–{to}',
  'bank.discardThese': 'Bu {count} kaydı ele',
  'bank.discardTitle': 'Üretilmiş soruları ele',
  'bank.discardBody':
    'Bu işlem, geçerli filtrelerle eşleşen {status} soruları kalıcı olarak siler{unfiltered}.',
  'bank.discardUnfiltered': ' — filtresiz, hepsini',
  'bank.willGo': '{count} soru silinecek.',
  'bank.willGo_one': '1 soru silinecek.',
  'bank.counting': 'Sayılıyor…',
  'bank.publishedSafe':
    'Yayındaki sorulara asla dokunulmaz: bir öğrenen bunlardan biriyle ölçülmüş olabilir ve satırı silmek bunu yeniden yazmak olurdu. Onları bunun yerine emekliye ayırın.',
  'bank.discard': 'Ele',
  'bank.noMatch': 'Bu filtrelerle eşleşen soru yok.',

  // ─── Generate ────────────────────────────────────────────────────────────
  // Turkish does not inflect a noun after a numeral, so the singular and plural
  // forms below are intentionally identical.
  'generate.forSkill': 'Bir yetkinlik için üret',
  'generate.forSkillTip':
    'Bu yetkinliğin hedefinin altındaki her hücresini doldurur — üç zorluk seviyesinin hepsi, her birinde en az bir çoktan seçmeli. Hedef, kimsenin talep etmediği bir yetkinlik için {floor} olup talebe göre büyür; yani iki yetkinlik için aynı sayı değildir. Kaç tane yazılacağı sizin yazdığınız bir sayı değil, eksiktir. İş kuyruğa alınır: taslaklar üretildikçe inceleme kuyruğuna düşer.',
  'generate.forSkillHint': 'Bir yetkinlik seçin. Zorluk, tür ve adet onun kapsam hedefinden gelir.',
  'generate.run': 'Üret',
  'generate.missing': '{cells} hücrede toplam {count} soru eksik.',
  'generate.missing_one': '{cells} hücrede {count} soru eksik.',
  'generate.queued': '{cells} hücrede {jobs} üretim işi kuyruğa alındı',
  'generate.queued_one': '{cells} hücrede {jobs} üretim işi kuyruğa alındı',
  'generate.nothingToQueue': 'Kuyruğa alınacak bir şey yok — bu yetkinlik zaten hedefinde',
  'generate.cellLine': '{difficulty} · {deficit} eksik · {steps}',
  'generate.draftsArrive':
    'Taslaklar üretildikçe inceleme kuyruğunda görünür — bir insan onaylamadan öğrenenlere asla gösterilmez.',

  'generate.bulkTitle': 'En çok istenen hücreleri doldur',
  'generate.bulkTip':
    'Aynı sıralamanın tepesini alır ve o hücreleri doldurur; böylece listeyi tek tek yetkinlik gezmeniz gerekmez. Önce önizleyin — bedava ve kaç taslağın üretileceğini tam olarak söyler.',
  'generate.bulkSummary': 'Hedefin altında {cells} hücre, toplam {deficit} soru eksik.',
  'generate.bulkSummary_one': 'Hedefin altında {cells} hücre, toplam {deficit} soru eksik.',
  'generate.cellsToFill': 'Doldurulacak hücre',
  'generate.cellsToFillHint': 'En yüksek sıradan başlayarak',
  'generate.preview': 'Önizle',
  'generate.queue': 'Kuyruğa al',
  'generate.queueJobs': '{count} işi kuyruğa al',
  'generate.queueJobs_one': '{count} işi kuyruğa al',
  'generate.previewLine':
    '{cells} hücre · üretilecek {drafts} taslak. Henüz hiçbir şey kuyruğa alınmadı.',
  'generate.previewLine_one':
    '{cells} hücre · üretilecek {drafts} taslak. Henüz hiçbir şey kuyruğa alınmadı.',
  'generate.previewCell': '{skill} · {difficulty} · {deficit} eksik',
  'generate.previewMore': '…ve {count} tane daha.',
  'generate.queuedBulk':
    '{cells} hücrede {jobs} iş kuyruğa alındı. Taslaklar üretildikçe inceleme kuyruğuna düşer.',
  'generate.queuedBulk_one':
    '{cells} hücrede {jobs} iş kuyruğa alındı. Taslaklar üretildikçe inceleme kuyruğuna düşer.',

  'generate.thinnest': 'Bankanın en ince olduğu yer',
  'generate.thinnestTip':
    'Hedefinin altındaki hücreler, eksik × talep ile sıralanır: talep, yetkinliği beyan eden öğrenen sayısı artı onu hedefleyen yol haritası sayısıdır; yaygınlık yalnızca beraberlik bozar. Aynı talep hedefi de yükseltir — istenen bir yetkinlik daha derin bir banka gerektirir, çünkü bir hücrede beş soru olması herkesin aynı beşi görmesi demektir. O yetkinliği seçmek için bir satıra tıklayın.',
  'generate.legend':
    'var = bu hücredeki yayında sorular · hedef = kaç tane olması gerektiği, {floor} artı talebin getirdiği · gerek = eksik · talep = beyan eden öğrenenler artı hedefleyen yol haritaları.',
  'generate.cellCounts': 'var {published} / {target} · gerek {deficit}{demand}',
  'generate.demandSuffix': ' · talep {demand}',
  'generate.select': 'Seç',

  // ─── Review queue ────────────────────────────────────────────────────────
  'queue.waiting': 'İnceleme bekleyen',
  'queue.approvalRate': 'İnsanın gördüklerinden onaylanan',
  'queue.approvalRateHint': 'Düşük: hakem fazla hoşgörülü. Çok yüksek: fazla katı.',
  'queue.stoppedBefore': 'İncelemeden önce durdurulan',
  'queue.stoppedBeforeHint': 'Reddedildi, asla silinmedi — incelenebilir.',
  'queue.empty': 'İnceleme bekleyen bir şey yok.',
  'queue.position': '{total} kayıttan {index}.',
  'queue.judgePercent': 'hakem %{percent}',
  'queue.nearDuplicate': 'yakın kopya',
  'queue.strongAnswer': 'Güçlü cevap: {answer}',
  'queue.judgeSaid': 'Hakem ne dedi',
  'queue.notePlaceholder': 'İsteğe bağlı not — aşağıdaki sebeplerin kapsamadığı durumlar için',
  'queue.approve': 'Onayla',
  'queue.keyHelp':
    'a onaylar · 1–9 bir sebeple reddeder · u geri adım atar. Siz uygulayana kadar hiçbir şey gönderilmez.',
  'queue.end': 'Kuyruğun sonu — kararlarınızı uygulayın.',
  'queue.apply': '{count} kararı uygula',
  'queue.apply_one': '{count} kararı uygula',
  'queue.discard': 'Vazgeç',
  'queue.skipped': '{count} tanesi uygulanmadı',
  'queue.reasonsTitle': 'Taslaklar neden geri çevriliyor',
  'queue.reasonsHint':
    'Tekrar eden bir sebep, prompt şablonunu değiştirme talimatıdır — ve etiketlenmiş bir başarısızlıktır; bir eval vakası da bundan yapılır.',

  // ─── Launch readiness ────────────────────────────────────────────────────
  'launch.title': 'Çıkışa hazır mıyız',
  'launch.state.pass': 'geçti',
  'launch.state.blocked': 'bloke',
  'launch.state.waived': 'istisna',
  'launch.state.info': 'bilgi',
  'launch.noBlockers': 'Bloke eden bir kapı yok.',
  'launch.blockers': '{count} kapı bloke ediyor: {names}.',
  'launch.readyNote':
    'İstisna yazılmış kapılar varsa aşağıda görünür — geçti demek değil, bilerek açık bırakıldı demek.',
  'launch.notReadyNote': 'Aşağıdaki iş listesi bunun neden böyle olduğunu satır satır söylüyor.',
  'launch.expiredTitle': 'Süresi dolmuş istisna',
  'launch.expiredBody':
    'Bu kapılar bir kez bilerek açık bırakıldı ve istisnanın süresi doldu. Kapı yeniden bloke ediyor.',
  'launch.expiredLine': '{key} · {owner} · {date} tarihinde doldu',
  'launch.scorecard': 'Scorecard',
  'launch.scorecardHint':
    'Boyut başına hedef ve mevcut değer. Hepsi veriden hesaplanır; elle değiştirilebilen tek şey istisnadır.',
  'launch.waiverLine': 'İstisna: {reason} — {owner}, {date} tarihine kadar',
  'launch.writeWaiver': 'İstisna yaz',
  'launch.worklist': 'Yapılacaklar',
  'launch.worklistHint':
    "Scorecard'dan türetilir, tiklenmez. Bir satır, işi yapıldığı an kendiliğinden kapanır; yarın bir madde emekli edilirse yeniden açılır.",
  'launch.noWork': 'Açık iş yok.',
  'launch.colTodo': 'Yapılacak',
  'launch.colDimension': 'Boyut',
  'launch.colDeficit': 'Eksik',
  'launch.colScreen': 'Ekran',
  'launch.openWaivers': 'Açık istisnalar',
  'launch.openWaiversHint': 'Bilerek açık bırakılmış kapılar. Geri almak da bir karardır.',
  'launch.until': '{owner} · {date} tarihine kadar',
  'launch.revoke': 'Geri al',
  'launch.waiverModalTitle': 'İstisna: {title}',
  'launch.waiverIntro':
    'Bu kapı açıkken çıkmaya karar veriyorsun. Kayıt kalıcı: süresi dolduğunda kapı yeniden bloke eder ve dolmuş istisna listede görünmeye devam eder.',
  'launch.reason': 'Gerekçe',
  'launch.reasonHint': 'Altı ay sonra bunu okuyan kişi neden böyle karar verdiğimizi anlamalı.',
  'launch.owner': 'Sahip',
  'launch.ownerHint': 'Bu kararın arkasında duran kişi.',
  'launch.expiresAt': 'Son kullanma',
  'launch.expiresAtHint': 'YYYY-AA-GG. Bu tarihten sonra kapı yeniden bloke eder.',
  'launch.submitWaiver': 'İstisnayı yaz',

  // ─── Skills ──────────────────────────────────────────────────────────────
  'skills.intro':
    'Yetkinlikler soruları konuya göre gruplar (üretici, kapsam boşlukları ve yol haritaları tarafından kullanılır). Bir yetkinlik ancak hiç sorusu kalmadığında silinebilir — aksi hâlde silmek tüm bankasını da silerdi.',
  'skills.new': 'Yeni yetkinlik',
  'skills.searchPlaceholder': 'Başlık veya slug — örn. kafka, postgres, react',
  'skills.originAll': 'Tümü',
  'skills.originCurated': 'Yazılmış',
  'skills.originCatalog': 'Katalog',
  'skills.originFromCvs': "CV'lerden",
  'skills.badge.curated': 'yazılmış',
  'skills.badge.stackoverflow': 'katalog',
  'skills.badge.user_generated': "bir CV'den",
  'skills.noMatch': 'Eşleşen yetkinlik yok.',
  'skills.showing': '{total} kayıttan {shown} tanesi gösteriliyor.',
  'skills.colTitle': 'Başlık',
  'skills.colSlug': 'Slug',
  'skills.colOrigin': 'Kaynak',
  'skills.colParent': 'Üst',
  'skills.colQuestions': 'Soru',
  'skills.root': 'kök',
  'skills.deleteBlocked': '{count} sorusu var — önce onları taşıyın veya silin',
  'skills.deleteConfirm': '“{title}” yetkinliği silinsin mi? Bu geri alınamaz.',
  'skills.none': 'Henüz yetkinlik yok.',
  'skills.editTitle': 'Yetkinliği düzenle',
  'skills.newTitle': 'Yeni yetkinlik',
  'skills.title': 'Başlık',
  'skills.slug': 'Slug',
  'skills.slugHint': 'Tire ile birleştirilmiş küçük harfli kelimeler. Yetkinlikler arasında tekil.',
  'skills.parent': 'Üst yetkinlik',
  'skills.parentHint': 'Üst düzey bir yetkinlik için boş bırakın.',
  'skills.parentPlaceholder': 'Üst yok (kök)',
  'skills.description': 'Açıklama',
  'skills.create': 'Oluştur',

  // ─── Item models ─────────────────────────────────────────────────────────
  'itemModels.writeTitle': 'Bir soru şablonu yaz',
  'itemModels.writeTip':
    'Şablon, içinde boşluklar olan bir sorudur. Yuvalar her boşluğun ne alabileceğini, kısıtlar hangi kombinasyonların anlamlı olduğunu söyler. Önizleme bedavadır ve hiçbir çağrı yapmaz — döngüyü orada kurun. Üretim son adımdır.',
  'itemModels.writeHint':
    'Gövdeyi ve neyin değişebileceğini siz yazarsınız. Üretici boşlukları doldurur ve yalnızca ipuçlarını, güçlü cevabı ve rubriği yazar — sorunun kendisini asla.',
  'itemModels.title': 'Başlık',
  'itemModels.titlePlaceholder': 'Eksik await',
  'itemModels.stem': 'Gövde',
  'itemModels.stemHint': 'Her boşluk için {SLOT} kullanın. BÜYÜK_ALT_ÇİZGİ adları.',
  'itemModels.stemPlaceholder':
    'Bir geliştirici {API}() çağrısının hemen ardından bir değeri logluyor ve {SYMPTOM} görüyor. Neden ve nasıl düzeltirsiniz?',
  'itemModels.slots': 'Yuvalar',
  'itemModels.slotsHint': 'Her boşluğun ne alabileceği',
  'itemModels.constraints': 'Kısıtlar',
  'itemModels.constraintsHint':
    'Hangi kombinasyonların tutarlı olduğu. Yuvalar serbestçe değişiyorsa [].',
  'itemModels.concepts': 'Doğru bir cevabın göstermesi gerekenler',
  'itemModels.conceptsHint': 'Her satıra bir tane. Bu, birkaç kelimeyle bilişsel modeldir.',
  'itemModels.previewFree': 'Önizle — bedava',
  'itemModels.saveActive': 'Etkin olarak kaydet',
  'itemModels.badJson': 'Yuvalar veya kısıtlar geçerli JSON değil: {message}',
  'itemModels.fixFirst': 'Önce bunları düzeltin',
  'itemModels.yield': 'Bu şablondan {count} tutarlı soru',
  'itemModels.yield_one': 'Bu şablondan {count} tutarlı soru',
  'itemModels.andMore': '…ve bu sırayla {count} tane daha.',
  'itemModels.listTitle': 'Şablonlar',
  'itemModels.listTip':
    'verim, şablonun üretebileceği tutarlı soru sayısıdır; üretilen ise gerçekte kaç taslağı olduğudur. Yazılmış ama hiç çalıştırılmamış bir şablon, hiç şablon olmamasıyla aynıdır.',
  'itemModels.none': 'Henüz şablon yok. Yukarıdaki form ilkini yazar.',
  'itemModels.status.draft': 'taslak',
  'itemModels.status.active': 'etkin',
  'itemModels.summary': 'verim {yield} · üretilen {produced} · {slots}{constraints}',
  'itemModels.noSlots': 'yuva yok',
  'itemModels.constraintCount': ' · {count} kısıt',
  'itemModels.items': 'Adet',
  'itemModels.generate': 'Üret',
  'itemModels.written':
    '{attempted} gövdeden {count} taslak yazıldı{extra}. İnceleme kuyruğundalar — bir insan onaylamadan hiçbiri öğrenene ulaşmaz.',
  'itemModels.written_one':
    '{attempted} gövdeden {count} taslak yazıldı{extra}. İnceleme kuyruğundalar — bir insan onaylamadan hiçbiri öğrenene ulaşmaz.',
  'itemModels.failedSuffix': ' · {count} tanesi tamamlanamadı',
  'itemModels.dupSuffix': ' · {count} yakın kopya',
  'itemModels.malformedSuffix': ' · {count} tanesi kapılarda reddedildi',

  // ─── Skill queue ─────────────────────────────────────────────────────────
  'skillQueue.mergeTitle': '“{title}” — birleştir',
  'skillQueue.mergeTarget': 'Hedef yetkinlik',
  'skillQueue.mergeTargetPlaceholder': 'Ara ve seç',
  'skillQueue.mergeExplain':
    '“{source}” satırı SİLİNMEYECEK — {target} hedefine yönlendirmeye dönüşecek.',
  'skillQueue.mergeDetail':
    "Bu yetkinliği CV'sinde belirtmiş {users} kullanıcının iddiası ve ona bağlı {questions} soru yerinde kalır; okuma yolu zinciri takip edip {target} hedefine varır. Bundan sonra aynı ismi yazan bir CV doğrudan {target} hedefine bağlanır.",
  'skillQueue.merge': 'Birleştir',
  'skillQueue.promoteTitle': '“{title}” — taksonomiye al',
  'skillQueue.heading': 'Başlık',
  'skillQueue.slug': 'Slug',
  'skillQueue.slugHint': 'Küçük harf, rakam ve tire. Benzersiz olmalı.',
  'skillQueue.parent': 'Üst yetkinlik',
  'skillQueue.parentPlaceholder': 'Kök seviye',
  'skillQueue.promoteNote': 'Satır curated olur — artık bizim yazdığımız bir taksonomi girdisidir.',
  'skillQueue.promote': 'Taksonomiye al',
  'skillQueue.emptyTitle': 'Kuyruk boş — taksonomi güncel.',
  'skillQueue.emptyBody':
    "CV'lerden gelen her yetkinlik ya kataloğumuzda karşılığını buldu ya da bir karara bağlandı. Yeni bir isim geldiğinde burada görünecek; o arada öğrenen beklemez, satır zaten kullanılıyor olur.",
  'skillQueue.banner':
    "Bu satırlar ŞU ANDA KULLANILIYOR — CV'den geldikleri anda kataloğa yazıldılar ve öğrenen hiçbir yerde beklemedi (BR-90). Burada verilen karar “bu var olsun mu” değil, “nereye ait”.",
  'skillQueue.count': '{count} yetkinlik karar bekliyor · en çok geçenler üstte',
  'skillQueue.colLabel': 'Etiket',
  'skillQueue.colUsage': 'Kullanım',
  'skillQueue.colQuestions': 'Soru',
  'skillQueue.colArrived': 'Geliş',
  'skillQueue.colDecision': 'Karar',
  'skillQueue.people': '{count} kişi',
  'skillQueue.accept': 'Kabul et',
  // ─── Business rules ──────────────────────────────────────────────────────
  'br.type.product': 'ürün',
  'br.type.productHint': 'Öğrenenin hissedebileceği bir ürün kararı.',
  'br.type.platform': 'platform',
  'br.type.platformHint': 'Sistemin bir sözü nasıl taşıdığı: zarflar, idempotency, telemetri.',
  'br.config.migrated': '⚙️ ayarlanabilir',
  'br.config.migratedHint':
    'Bir tercih: başka bir değer de savunulabilirdi. Config ekranından, deploy olmadan değiştirilir.',
  'br.config.hybrid': '⚙️ kısmen ayarlanabilir',
  'br.config.hybridHint':
    'Kuralın bir kısmı ayarlanabilir, bir kısmı kodda sabit — notta hangisinin hangisi olduğu yazar.',
  'br.config.in-code': 'kodda',
  'br.config.in-codeHint':
    'Tercih değil, algoritmanın tanımı. Başka bir değer ölçülmemiş bir şeyi iddia ederdi, o yüzden knob değil.',
  'br.config.invariant': '🔒 değişmez',
  'br.config.invariantHint':
    'Doğruluk kuralı — kimlik, teklik, akış. Ayarlanabilir olsaydı yanlış ayarlanabilirdi, o yüzden kodda zorlanır.',
  'br.config.unknown': '—',
  'br.config.unknownHint': 'Bu kuralın ayarlanabilirliği henüz sınıflandırılmadı.',
  'br.intro':
    'Prepzio’nun ürün/alan değişmezleri (BR-*), sürece göre gruplanmış. Kimliğe, kural metnine veya kaynağa göre arayın; sürece göre filtreleyin. PRE-125/126: kural metni ve türü depoda yazılır ve deploy ile uygulanır. Burada yapılan şey incelemedir — bir kişinin kuralı okuduğunu ve doğru olduğunu söylemesi. Cümleyi yeniden yazmak bu incelemeyi bayatlatır, çünkü inceleme değişen kelimelere verilmişti.',
  'br.infoTip':
    'Her kural, uygulandığı kaynağı (koddaki // BR-n yorumlarının dosya:satır ters bağı) ve çalışma zamanı ayar duruşunu gösterir: ⚙️ ayarlanabilir (Config üzerinden yöneticiye açık), kısmen ayarlanabilir, kodda veya 🔒 değişmez. Kimlikler asla yeniden kullanılmaz — silinen bir numara emekli kalır, çünkü kod yorumları ve blueprint çapaları hâlâ ona atıf yapar.',
  'br.loadFailed': 'Kural kataloğu yüklenemedi',
  'br.searchPlaceholder': 'id, kural metni veya kaynak…',
  'br.type': 'Tür',
  'br.allTypes': 'Tüm türler',
  'br.process': 'Süreç',
  'br.allProcesses': 'Tüm süreçler',
  'br.priority': 'Öncelik',
  'br.review': 'İnceleme',
  'br.reviewedByMe': 'Gözden geçirdiklerim',
  'br.awaitingReading': 'Okunmayı bekleyenler',
  'br.rejectedByMe': 'Reddettiklerim',
  'br.addingRule': 'Kural ekleme',
  'br.ruleText': 'Kural metni',
  'br.summary': '{total} kuraldan {shown} tanesi · {product} ürün · {platform} platform',
  'br.awaitingReview': '{count} tanesi incelemenizi bekliyor',
  'br.rejectedCount': '{count} reddedildi',
  'br.noMatch': 'Aramanızla eşleşen kural yok.',
  'br.colDecision': 'Karar',
  'br.colRule': 'Kural',
  'br.colConfig': 'Ayar',
  'br.rejected': 'Reddedildi',
  'br.iReviewedIt': 'Gözden geçirdim',
  'br.textChanged': 'Metin değişti — yeniden okunmalı',
  'br.undoDecision': 'Kararı geri al',
  'br.reject': 'Reddet',
  'br.rejectTitle': '{id} — reddet',
  'br.rejectReason': 'Gerekçe',
  'br.rejectReasonHint':
    'Zorunlu. Reddetmek, katalogdaki bu cümlenin ürünü doğru anlatmadığı iddiasıdır — bir ay sonra bu satırı okuyan kişi nedenini burada bulacak.',
  'br.reasonRequired': 'Gerekçe gerekli',

  // ─── Traceability ────────────────────────────────────────────────────────
  'rtm.status.ready': 'hazır',
  'rtm.status.todo': 'yapılacak',
  'rtm.status.gap': 'boşluk',
  'rtm.kind.step': 'Akış adımı',
  'rtm.kind.backlog': 'Backlog',
  'rtm.kind.rule': 'Ürün kuralı',
  'rtm.verdict.covered': 'Karşılandı',
  'rtm.verdict.partial': 'Kısmen',
  'rtm.verdict.not_covered': 'Karşılanmadı',
  'rtm.verdict.not_applicable': 'Kapsam dışı',
  'rtm.all': 'Hepsi',
  'rtm.intro':
    'Her gereksinim tekil bir kimlikle burada: akış adımları (F…), backlog maddeleri (B-…) ve ürün kuralları (R-…). Her satır hangi iş kuralına (BR-…), hangi kod dosyasına ve hangi teste bağlandığını gösterir. Kod bağları ve kod incelemesi türetilir, elle değiştirilemez; elle verilen tek karar kapsam kararıdır — bu yetenek gerçekten teslim edildi mi.',
  'rtm.apiUnreachable':
    'API’ye ulaşılamadı — matris dokümandan gösteriliyor, kapsam kararları yüklenmedi.',
  'rtm.stale':
    'Bu doküman en son {date} tarihinde kodla karşılaştırıldı ({days} gün önce, eşik {threshold} gün). Yeniden gözden geçirilmeli.',
  'rtm.traceability': 'İzlenebilirlik',
  'rtm.tracedHint': '{total} gereksinimden {traced} tanesi koda bağlı',
  'rtm.decided': 'Karar verilen',
  'rtm.decidedHint': '{covered} karşılandı · {partial} kısmen · {notCovered} hayır',
  'rtm.decidedFallback': 'kapsam kararları API’den gelir',
  'rtm.untraced': 'İzlenemeyen',
  'rtm.untracedHint': 'ne BR ne kod bağı var',
  'rtm.referencedBr': 'Atıf yapılan BR',
  'rtm.referencedBrHint': 'iş kuralı kataloğuna karşı doğrulanıyor',
  'rtm.statusSpread': 'Durum dağılımı',
  'rtm.searchPlaceholder': 'Ara: id, metin, BR-72, roadmap.ts …',
  'rtm.untracedOnly': 'Yalnızca izlenemeyenler',
  'rtm.allDecisions': 'Tüm kararlar',
  'rtm.myApprovals': 'Onayladıklarım',
  'rtm.awaitingMe': 'Kararımı bekleyenler',
  'rtm.rowCount': '{total} satırdan {shown} tanesi',
  'rtm.colId': 'ID',
  'rtm.colKind': 'Tür',
  'rtm.colRequirement': 'Gereksinim',
  'rtm.colBusinessRule': 'İş kuralı',
  'rtm.colCodeTest': 'Kod / test',
  'rtm.colDecision': 'Kapsam kararı',
  'rtm.noBr': 'BR yok',
  'rtm.decide': 'Karar ver',
  'rtm.decisionTitle': '{id} — kapsam kararı',
  'rtm.reason': 'Gerekçe',
  'rtm.reasonHint':
    '“Karşılandı” dışındaki her karar için zorunlu — bir ay sonra bu satırı okuyan kişi nedenini burada bulacak.',
  'rtm.reasonRequired': 'Gerekçe gerekli',
  'rtm.clearDecision': 'Kararı sil',
  'rtm.howVerified': 'Bu matris nasıl doğrulanıyor',
  'rtm.howVerifiedBody':
    'pnpm check:docs her satırı denetler: kimlikler tekil mi, atıf yapılan her BR-… {catalog} içinde var mı, her kod bağı gerçek bir dosyaya (ve satır numarası verildiyse gerçek bir satıra) çözülüyor mu, backlog ve release dilimlerinin gösterdiği kimlikler mevcut mu. Kod taşındığında CI kırılır — böylece doküman sessizce bayatlamaz. Bu bir varlık kontrolüdür: çapanın çözüldüğünü kanıtlar, arkasındaki kodun hâlâ doğru işi yaptığını değil. O ikinci soruyu yukarıdaki anlamsal inceleme cevaplar.',

  // ─── User flow ───────────────────────────────────────────────────────────
  'userFlow.loadFailed': 'Akış dokümanı yüklenemedi',
  'userFlow.intro':
    'Kullanıcı kaydolduğu andan mezuniyete kadar geçen akışın uçtan uca anlatımı: hangi ekranı görür, sistem hangi kararı verir, ilk yol haritası nasıl oluşur, günlük pratik döngüsü nasıl işler ve mezun olamazsa ne olur. Her faz için bir diyagram ve adım adım tablo var.',
  'userFlow.owner': 'Sahip:',
  'userFlow.verifiedAt': 'Son doğrulama:',
  'userFlow.staleAfter': '(eşik {days} gün)',
  'userFlow.adr': 'Karar dokümanı:',
  'userFlow.ruleCatalog': 'Kural kataloğu:',
  'userFlow.currentDecision': 'Güncel ürün kararı — {date}',
  'userFlow.readingGuide': '00 · Diyagramları okuma rehberi',
  'userFlow.readingGuideHint':
    'Tüm diyagramlarda aynı aktörler var. Adım tablolarındaki durum etiketi, o adımın bugün çalışıp çalışmadığını gösterir.',
  'userFlow.worksToday': 'bugün çalışıyor',
  'userFlow.toBuild': 'geliştirilecek',
  'userFlow.brokenToday': 'bugün yanlış davranıyor, düzeltilmeli',
  'userFlow.personas': '01 · Kim geliyor — 4 persona',
  'userFlow.personasHint':
    'Herkes aynı zorunlu CV adımından geçiyor. Fark: iş ilanı ekliyorlar mı, kaç tane, ve CV’leri gerçek seviyelerini ne kadar kanıtlıyor?',
  'userFlow.day0': 'Gün 0: {signal}',
  'userFlow.bigPicture': '02 · Büyük resim — uçtan uca akış',
  'userFlow.bigPictureHint':
    'Detaya inmeden önce tüm yolculuğun tek bakışta hâli. Sonraki bölümlerde her kutunun içini açıyoruz.',
  'userFlow.noOverview': 'Genel bakış diyagramı bu dokümanda tanımlı değil.',
  'userFlow.phaseTitle': '{index} · {num} — {title}',
  'userFlow.colId': 'ID',
  'userFlow.colWho': 'Kim',
  'userFlow.colWhat': 'Ne oluyor',
  'userFlow.colRuleNote': 'Kural / not',
  'userFlow.colTrace': 'İzlenebilirlik',
  'userFlow.untraceable': 'izlenemiyor',
  'userFlow.testCount': '✓ {count} test',
  'userFlow.flowStep': 'Akış adımı',
  'userFlow.productRule': 'Ürün kuralı',
  'userFlow.rulesTitle': '{index} · Ürün kuralları',
  'userFlow.rulesHint': 'Akışın her yerinde geçerli olan, tartışmaya kapalı kurallar.',
  'userFlow.colRule': 'Kural',
  'userFlow.colSays': 'Ne diyor',
  'userFlow.colWhy': 'Neden böyle',
  'userFlow.colBusinessRule': 'İş kuralı',
  'userFlow.colCodeAnchor': 'Kod bağı',
  'userFlow.notEnforced': 'uygulanmıyor',
  'userFlow.noAnchor': 'çapasız — incelenemiyor',
  'userFlow.coverageTitle': '{index} · Kapsama sorunu',
  'userFlow.coverageHint':
    'Bugünkü konu kataloğu 5 başlıktan oluşuyor ve dar. CV zorunlu olunca bu sorun büyüyecek, çünkü artık herkesin gerçek becerileri analizden geçecek.',
  'userFlow.inCatalogToday': 'Bugün katalogda olan',
  'userFlow.missingFromCatalog': 'CV ve ilanlarda sık geçen ama katalogda olmayanlar:',
  'userFlow.approachTitle': 'Çözüm yaklaşımı',
  'userFlow.approachBody':
    'Katalogu elle büyütmek yerine talebi kaynağından okuyoruz: CV ve iş ilanları zaten hangi becerilerin gerektiğini söylüyor.',
  'userFlow.coverageWarning': 'CV zorunlu kararının doğrudan sonucu',
  'userFlow.backlogTitle': '{index} · Ne hazır, ne yapılacak',
  'userFlow.backlogHint':
    'Akışın hangi kısmı bugün çalışıyor, hangi kısmı bu kararla birlikte geliştirilecek.',
  'userFlow.colStep': 'Adım',
  'userFlow.colNote': 'Not',
  'userFlow.slices': 'Yayına alma dilimleri',
  'userFlow.slicesHint':
    'Her dilim tek başına kullanıcıya bir sonuç üretmeli — yoksa dilim değil, yığın olur.',

  // ─── Users ───────────────────────────────────────────────────────────────
  'users.intro':
    'Her öğrenen: profil · başlangıç yanıtları · değerlendirme soruları ve cevapları · yol haritaları. Tüm resmi görmek için bir kullanıcıya tıklayın.',
  'users.infoTip':
    'Yalnızca gerçek öğrenenler — simülasyon kullanıcıları (@sim.prepzio.local) gizlidir. Bir kullanıcıyı açmak, başlangıç profilini, sorular ve verdiği cevaplarla birlikte her pratik oturumunu ve üretilen yol haritalarına bağlantıları gösterir.',
  'users.colUser': 'Kullanıcı',
  'users.colPlan': 'Plan',
  'users.colPersona': 'Persona',
  'users.colRoadmaps': 'Yol haritası',
  'users.colJoined': 'Katılım',
  'users.noOnboarding': 'başlangıç yok',
  'users.none': 'Kullanıcı yok.',
  'users.allUsers': 'Tüm kullanıcılar',
  'users.verified': 'doğrulanmış',
  'users.joined': '{date} tarihinde katıldı',
  'users.llmTraces': 'LLM izleri',
  'users.subscription': 'Abonelik',
  'users.plan': 'Plan',
  'users.active': 'etkin',
  'users.inactiveFree': 'etkin değil (ücretsiz)',
  'users.product': 'Ürün',
  'users.store': 'Mağaza',
  'users.expires': 'Bitiş',
  'users.willRenew': 'Yenilenecek',
  'users.onboardingProfile': 'Başlangıç profili',
  'users.experience': 'Deneyim',
  'users.goal': 'Hedef',
  'users.aiHabit': 'Yapay zekâ kullanım alışkanlığı',
  'users.completed': 'Tamamlanma',
  'users.weakAreasSelf': 'Zayıf alanlar (kendi beyanı)',
  'users.aiTools': 'Yapay zekâ araçları',
  'users.noOnboardingYet': 'Bu kullanıcı başlangıç akışını tamamlamadı.',
  'users.measuredWeak': 'Ölçülmüş zayıf yetkinlikler',
  'users.measuredWeakTip':
    'Yürüyen ustalığı — puanlanmış her değerlendirme ve pratik cevabı üzerinden yakınlık ağırlıklı kavram ustalığı (hafif bilgi izleme) — yol haritasının zayıflık eşiğinin ({threshold}) altında kalan yetkinlikler. Bu, yukarıdaki kendi beyan edilen başlangıç zayıf alanlarından farklı olarak, yol haritasının hedeflediği sinyalin ta kendisidir.',
  'users.noGraded':
    'Henüz puanlanmış cevap yok — zayıf yetkinlikleri ölçmeye yetecek veri yok. (Yukarıdaki zayıf alanlar kullanıcının kendi beyan ettiği başlangıç yanıtlarıdır.)',
  'users.noneBelowThreshold':
    '{count} puanlanmış cevap içinde zayıflık eşiğinin ({threshold}) altında yetkinlik yok — ölçülmüş zayıflık yok.',
  'users.noneBelowThreshold_one':
    '{count} puanlanmış cevap içinde zayıflık eşiğinin ({threshold}) altında yetkinlik yok — ölçülmüş zayıflık yok.',
  'users.weakestFirst': 'En zayıf üstte · ustalık < {threshold} · {count} puanlanmış cevaptan.',
  'users.weakestFirst_one': 'En zayıf üstte · ustalık < {threshold} · {count} puanlanmış cevaptan.',
  'users.weakSkill': '{title} · {mastery}/100 · {count}×',
  'users.sessions': 'Oturumlar ve cevaplar ({count})',
  'users.noSessions': 'Henüz pratik veya değerlendirme oturumu yok.',
  'users.sessionMeta': '{date} · {count} soru',
  'users.sessionMeta_one': '{date} · {count} soru',
  'users.roadmaps': 'Yol haritaları ({count})',
  'users.noRoadmaps': 'Bu kullanıcı için henüz yol haritası üretilmedi.',
  'users.dangerZone': 'Tehlikeli bölge',
  'users.dangerTip':
    'Tüm öğrenen verisini siler — başlangıç profili, tercihler, değerlendirmeler, pratik oturumları, yol haritaları, ustalık/geri bildirim, CV/iş ilanı belgeleri, maliyet ve olay geçmişi. Oturum açma korunur: kullanıcı yine giriş yapabilir ama sıfırdan yeni bir kayıt gibi başlar (başlangıcı tekrar yapar). Geri alınamaz. Yalnızca admin.resetAllowedEmails ayarında listelenen hesaplar sıfırlanabilir — oraya eklediğinizde kendi yönetici hesabınız dahil.',
  'users.dangerHint':
    'Tüm öğrenen verisini sıfırla ama hesabın oturum açmasını koru (yeni kayıt durumu).',
  'users.notAllowlisted':
    '{email} sıfırlama izin listesinde değil, bu yüzden API bunu reddedecek (403).',
  'users.addToAllowlist':
    'Ayarlar → admin.resetAllowedEmails altına ekleyin. Liste varsayılan olarak boştur; böylece biri hangi hesapların silinebileceğini söylemeden hiçbir gerçek hesap silinemez.',
  'users.resetButton': 'Tüm kullanıcı verisini sıfırla',
  'users.resetTitle': 'Tüm kullanıcı verisi sıfırlansın mı?',
  'users.resetBody':
    'Bu işlem {email} için her öğrenen kaydını siler (başlangıç, oturumlar, yol haritaları, ustalık, geri bildirim, belgeler, geçmiş). Oturum açma korunur — tekrar giriş yapabilirler ama yepyeni bir kayıt olarak gelirler. Bu geri alınamaz.',
  'users.typeEmail': 'Onaylamak için kullanıcının e-postasını yazın:',
  'users.resetConfirm': 'Veriyi sıfırla',

  // ─── Simulate ────────────────────────────────────────────────────────────
  'sim.intro':
    'Sentetik bir persona için baştan sona taslak yol haritası üretin (başlangıç → LLM ile yazılmış cevaplarla değerlendirme → gerçek puanlama → yol haritası). Tam üretim hattını çalıştırır ama tek kullanımlık bir kullanıcı üzerinde — hiçbir gerçek öğrenen etkilenmez ve sonuç simülasyon olarak işaretlenir.',
  'sim.infoTip':
    'Persona gerçek hattan geçer: sentetik bir kullanıcı + başlangıç profili → cevaplarını LLM’in karakterde yazdığı bir başlangıç değerlendirmesi → üretim puanlayıcısı → yol haritası üreticisi. Personanın her pratik durağını da cevaplayıp puanlanması için “Yol haritasını da tamamla” kutusunu işaretleyin. Her şey tek kullanımlık bir @sim.prepzio.local kullanıcısı altındadır ve buradan silinebilir.',
  'sim.copyPersona': 'Personayı bir kullanıcıdan kopyala (isteğe bağlı)',
  'sim.blankPersona': '— Boş bir personayla başla —',
  'sim.loadingUsers': 'Kullanıcılar yükleniyor…',
  'sim.experience': 'Deneyim seviyesi',
  'sim.goal': 'Hedef',
  'sim.aiHabit': 'Yapay zekâ kullanım alışkanlığı',
  'sim.weakAreas': 'Zayıf alanlar',
  'sim.questionCount': 'Değerlendirme sorusu',
  'sim.label': 'Etiket (isteğe bağlı)',
  'sim.labelPlaceholder': 'örn. junior · hata ayıklama boşluğu',
  'sim.run': 'Simülasyonu çalıştır',
  'sim.alsoComplete':
    'Yol haritasını da tamamla (persona her pratik durağını cevaplar, baştan sona puanlanır)',
  'sim.alsoCompleteHint':
    'Daha yavaştır ve daha fazla LLM çağrısı kullanır (her durak cevaplanır ve puanlanır). Önce banka: yetkinliğinde yayında sorusu olmayan duraklar atlanır.',
  'sim.badge': '🧪 simülasyon',
  'sim.items': '{count} madde',
  'sim.aiDep': 'YZ bağımlılığı {score}',
  'sim.explanation': 'açıklama {score}',
  'sim.concept': 'kavram {score}',
  'sim.answered': '{count} cevaplandı',
  'sim.roadmapCompleted': '✓ yol haritası tamamlandı',
  'sim.roadmapPartial': 'yol haritası kısmi',
  'sim.stationsPractised': '{count} durak çalışıldı',
  'sim.practiceAnswers': '{count} pratik cevabı',
  'sim.skippedNoBank': '{count} atlandı (banka yok)',
  'sim.hideSource': 'Kaynağı gizle',
  'sim.showSource': 'Mermaid kaynağı',
  'sim.deleteSim': 'Simülasyonu sil',
  'sim.costTitle': 'Bu çalışmanın LLM maliyeti',
  'sim.costCalls': '{count} çağrı',
  'sim.costTokens': '{count} token',
  'sim.colPurpose': 'Amaç',
  'sim.colCalls': 'Çağrı',
  'sim.colTokens': 'Token',
  'sim.colCost': 'Maliyet',
  'sim.loadingRoadmap': 'Yol haritası yükleniyor…',

  'sim.loopTitle': 'Mezuniyet ve yeniden döngü simülasyonu',
  'sim.loopTip':
    'Aşağıdaki sentetik yetkinlik bazlı ustalığı gerçek mezuniyet değerlendiricisine (BR-72) ve yeniden döngüde gerçek kural tabanlı yol haritası üreticisine (BR-56) besler. Ustalık taban değerden başlar ve her döngüde +/döngü kadar artar; böylece eninde sonunda eşiği aşar. Her adım hangi kuralın çalıştığını gösterir.',
  'sim.baseMastery': 'Taban ustalık',
  'sim.perLoop': '+ döngü başına',
  'sim.maxLoops': 'En fazla döngü',
  'sim.simulateLoop': 'Döngüyü simüle et',
  'sim.loops': '{count} döngü',
  'sim.loops_one': '{count} döngü',
  'sim.graduatedAt': ' · {loop}. döngüde mezun oldu',
  'sim.loopStep': '{loop}. döngü · {outcome}',
  'sim.nextOptions': 'Sonraki seçenekler — seviye atlama: {levelUp} · yeni patika: {tracks}',
  'sim.outcome.graduated': 'mezun oldu',
  'sim.outcome.reloop': 'yeniden döngü',
  'sim.outcome.stalled': 'takıldı',

  // ─── Costs ───────────────────────────────────────────────────────────────
  'costs.intro':
    'llm_calls defterinden canlı LLM harcaması — her eğitmen, puanlama, yazım, yol haritası ve CV/iş ilanı çağrısı. Buradaki hiçbir şey sabit yazılmadı.',
  'costs.infoTip':
    'Her model çağrısı deftere bir satır yazar (amaç · sağlayıcı · model · token · gerçek USD maliyet). Tüm zamanlar bütün defterdir; Son 30 gün ve Ay başından bugüne çağrı tarihine göre süzer. Bütçe üst sınırı yalnızca yazım/üretim katmanını yönetir, ama gösterilen ay başından bugüne tüm amaçlardaki toplam harcamadır.',
  'costs.allTime': 'Tüm zamanlar',
  'costs.lastDays': 'Son {days} gün',
  'costs.monthToDate': 'Ay başından bugüne',
  'costs.tokensAllTime': 'Token (tüm zamanlar)',
  'costs.callsSub': '{count} çağrı',
  'costs.tokensSub': '{in} giriş · {out} çıkış',
  'costs.budget': 'Aylık yazım bütçesi',
  'costs.budgetNote':
    'Üst sınır yazım/üretim katmanını yönetir; gösterilen ay başından bugüne tüm amaçlardaki toplam harcamadır.',
  'costs.byArea': 'Alana göre harcama',
  'costs.byAreaTip':
    'Öğrenene dönük harcama, ürünü kullanan kişi sayısıyla büyür — başlangıç, pratik ve planlama. Bize ait olan büyümez: içerik yazımı soru başına bir kez, bir yönetici tıklamasıyla ödenir; dahili araçlar ise kendi enstrümanlarımızın kendi repomuza çevrilmiş hâlidir ve hiçbir öğrenen onları beklemez.',
  'costs.learnerFacing': 'Öğrenene dönük',
  'costs.learnerFacingSub': 'aralığın {share} kadarı · kullanıcıyla ölçeklenir',
  'costs.ours': 'Bize ait (içerik + araçlar)',
  'costs.oursSub': 'aralığın {share} kadarı · kendi faaliyetimizle ölçeklenir',
  'costs.colArea': 'Alan',
  'costs.colCost': 'Maliyet',
  'costs.colShare': 'Pay',
  'costs.colCalls': 'Çağrı',
  'costs.colPurposes': 'Amaçlar',
  'costs.noSpend': 'Bu aralıkta harcama yok.',
  'costs.byPurpose': 'Amaca göre harcama',
  'costs.colPurpose': 'Amaç',
  'costs.colInTokens': 'Giriş token',
  'costs.colOutTokens': 'Çıkış token',
  'costs.noCalls': 'Henüz kaydedilmiş LLM çağrısı yok.',
  'costs.byModel': 'Modele göre harcama',
  'costs.colModel': 'Model',
  'costs.byStatus': 'Duruma göre çağrılar',
  'costs.statusLine': '{count} çağrı · {cost}',
  'costs.daily': 'Günlük harcama (son {days} gün)',
  'costs.dayTooltip': '{day}: {cost} · {calls} çağrı',
  'costs.recent': 'Son çağrılar',
  'costs.recentTip':
    'En yeni 50 defter satırı. LLM izi, o span için Datadog LLM Observability’yi açar. Span kimlikleri saklanmadan önce alınan satırların bağlantısı yoktur.',
  'costs.colWhen': 'Ne zaman',
  'costs.colLatency': 'Gecikme',
  'costs.colTrace': 'İz',
  'costs.llmTrace': 'LLM izi',
  'costs.noPurposeDescription':
    'Bu amaç için tanım yazılmamış — LLM_PURPOSE_META (@prepzio/shared) içine ekleyin.',
  'costs.status.ok': 'tamam',
  'costs.status.error': 'hata',
  'costs.status.rate_limited': 'hız sınırı',

  // ─── Emails ──────────────────────────────────────────────────────────────
  'email.intro':
    'Düz bir mesaj ya da yayımlanmış bir Resend şablonu gönderin. Teslimat güncellemeleri imzalı webhook’larla gelir.',
  'email.compose': 'Oluştur',
  'email.composeMode': 'Oluşturma modu',
  'email.plain': 'Düz metin',
  'email.template': 'Şablon',
  'email.recipient': 'Alıcı',
  'email.publishedTemplate': 'Yayımlanmış şablon',
  'email.selectTemplate': 'Bir şablon seçin…',
  'email.noTemplates': 'Yayımlanmış şablon yüklenmedi (Full Access API anahtarı gerekir).',
  'email.subject': 'Konu',
  'email.subjectPlaceholder': 'E-posta konusu',
  'email.message': 'Mesaj',
  'email.messagePlaceholder': 'E-posta gövdesini yazın…',
  'email.appendHtml': 'Mesajın altına HTML ekle',
  'email.htmlLabel': 'HTML (metin mesajından sonra eklenir)',
  'email.loadingVariables': 'Şablon değişkenleri yükleniyor…',
  'email.templateMeta': 'Konu: {subject} · Gönderen: {from}',
  'email.fromDefault': 'EMAIL_FROM varsayılanı',
  'email.send': 'E-posta gönder',
  'email.newKey': 'Yeni anahtar',
  'email.fillRequired': 'Zorunlu değişkenleri doldurun: {keys}',
  'email.mustBeNumber': '{key} değişkeni sayı olmalı',
  'email.subjectAndTextRequired': 'Konu ve metin gövdesi zorunludur',
  'email.confirmTemplate': '“{name}” şablonu {to} adresine gönderilsin mi?',
  'email.confirmPlain': 'Düz e-posta {to} adresine gönderilsin mi?',
  'email.replayed': 'Mevcut gönderim tekrarlandı {id} ({status})',
  'email.sent': 'Gönderildi — id {id} ({status})',
  'email.history': 'Gönderim geçmişi',
  'email.filterByEmail': 'E-postaya göre filtrele…',
  'email.loadingHistory': 'Geçmiş yükleniyor…',
  'email.noSends': 'Henüz gönderim yok.',
  'email.plainLabel': 'Düz metin',
  'email.resendId': 'Resend: {id}',
  'email.loadMore': 'Daha fazla yükle',
  'email.status.queued': 'kuyrukta',
  'email.status.sent': 'gönderildi',
  'email.status.delivered': 'iletildi',
  'email.status.delivery_delayed': 'gecikti',
  'email.status.bounced': 'geri döndü',
  'email.status.complained': 'şikâyet',
  'email.status.failed': 'başarısız',
  'email.status.suppressed': 'engellendi',

  // ─── System health ───────────────────────────────────────────────────────
  'health.intro':
    'Son {hours} saat. LLM sayıları llm_calls defterinden gelir; monitörler, eğilimler, hatalar ve iş çalışmaları Datadog’dan gelir (ortam {env}).',
  'health.infoTip':
    'Telemetri ortam etiketi {env} (DD_ENV). Datadog, API tarafından okuma yetkili bir Application key ile sorgulanır — panel Datadog’u hiçbir zaman kendisi çağırmaz. Bu ekran günlük soruları yanıtlar; tek bir izi kazımak veya logları serbestçe aramak hâlâ Datadog’un işidir ve buradaki her satır oraya derin bağlantı verir.',
  'health.llmCalls': 'LLM çağrısı',
  'health.ledgerWindow': 'defter · {hours} saat',
  'health.llmErrors': 'LLM hatası',
  'health.llmErrorsSub': 'hata + hız sınırı',
  'health.fallbackRate': 'Yedeğe düşme oranı',
  'health.fallbackSub': '{calls} çağrıdan {fallbacks}',
  'health.llmP95': 'LLM p95 gecikme',
  'health.fromLedger': 'llm_calls kaydından',
  'health.httpP95': 'HTTP p95',
  'health.apm': 'Datadog APM',
  'health.llmSpend': 'LLM harcaması',
  'health.llmObs': 'Datadog LLM Obs',
  'health.openErrors': 'Açık hatalar',
  'health.errorTracking': 'Error Tracking',
  'health.notConfigured': 'Datadog okuması yapılandırılmamış',
  'health.notConfiguredBody':
    'Worker üzerinde DATADOG_APP_KEY (Application key) tanımlayın. Tek başına DATADOG_API_KEY yalnızca iz yazar.',
  'health.queryFailed': 'Datadog sorgusu başarısız',
  'health.monitors': 'Monitörler',
  'health.firing': '{count} tanesi alarm veriyor',
  'health.noMonitors': 'team:prepzio etiketli monitör yok.',
  'health.colMonitor': 'Monitör',
  'health.colState': 'Durum',
  'health.colChanged': 'Değişim',
  'health.requests': 'İstekler',
  'health.errors': 'Hatalar',
  'health.hourlyWindow': 'saatlik · son {hours} saat',
  'health.recentErrors': 'Son hatalar',
  'health.noErrorLogs': 'Bu aralıkta hata logu yok.',
  'health.colWhen': 'Ne zaman',
  'health.colRoute': 'Yol',
  'health.colCode': 'Kod',
  'health.colMessage': 'Mesaj',
  'health.colTrace': 'İz',
  'health.openTrace': 'aç',
  'health.jobs': 'Cron’lar ve kuyruklar',
  'health.noJobLogs': 'Bu aralıkta iş logu yok.',
  'health.colJob': 'İş',
  'health.colKind': 'Tür',
  'health.colLastRun': 'Son çalışma',
  'health.colRuns': 'Çalışma',
  'health.colErrors': 'Hata',
  'health.noIssues': 'Bu aralıkta açık Error Tracking kaydı yok.',
  'health.colError': 'Hata',
  'health.colEvents': 'Olay',
  'health.colLastSeen': 'Son görülme',
  'health.justNow': 'az önce',
  'health.minutesAgo': '{n} dk önce',
  'health.hoursAgo': '{n} sa önce',
  'health.daysAgo': '{n} gün önce',

  'skillQueue.acceptNote':
    'Kabul et: satır doğru, ama bizim yazacağımız bir taksonomi girdisi değil (şirket içi bir araç, niş bir kütüphane). Kuyruktan çıkar, user_generated olarak kalır — “bunu biz yazmadık” satır hakkında doğru bir bilgi ve ileride taksonomiyi gözden geçirene gerçek bir sinyal.',
};
